import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { STATUS_META, formatDate, waContactLink, PAYMENT_METHODS } from "@/lib/status";
import api, { fileUrl, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KIND_LABEL = { mao_de_obra: "Mão de obra", peca: "Peça", outro: "Outro" };
import {
  Wrench, Calendar, MapPin, User, Phone, ShieldCheck, ChevronDown, ChevronUp, Hash, Star, Store, AlertTriangle, CalendarClock, FileText, DollarSign, QrCode, Camera, MessageCircle, Film, CheckCircle2, Circle, CreditCard, Ticket, Loader2, XCircle, Handshake, HelpCircle,
} from "lucide-react";

const ACCEPTANCE_META = {
  pendente: { label: "Aguardando aceite", color: "bg-amber-100 text-amber-800 border-amber-200" },
  aceito: { label: "Aceito pelo cliente", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  duvida: { label: "Dúvida do cliente", color: "bg-blue-100 text-blue-800 border-blue-200" },
};

const STEPS = [
  { key: "aberto", label: "Aberto" },
  { key: "atribuido", label: "Atribuído" },
  { key: "a_caminho", label: "A caminho" },
  { key: "concluido", label: "Concluído" },
];
function stepIndex(status) {
  if (status === "aceito") return 1;
  const i = STEPS.findIndex((s) => s.key === status);
  return i;
}

function StatusStepper({ status, id }) {
  if (status === "cancelado") return null;
  const active = stepIndex(status);
  return (
    <div className="flex items-center" data-testid={`status-stepper-${id}`}>
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex flex-col items-center flex-shrink-0">
            {i <= active
              ? <CheckCircle2 className="h-4 w-4 text-[#1e7be0]" />
              : <Circle className="h-4 w-4 text-slate-300" />}
            <span className={`text-[10px] mt-0.5 ${i <= active ? "text-slate-600 font-medium" : "text-slate-400"}`}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-0.5 flex-1 mx-1 mb-3.5 ${i < active ? "bg-[#1e7be0]" : "bg-slate-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Row({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-2 text-sm text-slate-600">
      <Icon className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

export default function RequestCard({ request, actions, showCustomer, canPayOnline, customerView, paymentOptions, onChanged }) {
  const [open, setOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [quoteNote, setQuoteNote] = useState("");
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [acceptNote, setAcceptNote] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [acceptBusy, setAcceptBusy] = useState(false);
  const meta = STATUS_META[request.status] || { label: request.status, color: "bg-slate-100 text-slate-700" };
  const acc = request.customer_acceptance;
  const accMeta = acc ? ACCEPTANCE_META[acc] : null;

  const doAcceptance = async (action) => {
    if (action === "duvida" && !acceptNote.trim()) {
      toast.error("Descreva sua dúvida ou recomendação.");
      return;
    }
    setAcceptBusy(true);
    try {
      await api.patch(`/requests/${request.id}/acceptance`, {
        action, note: acceptNote.trim() || null, payment_method: payMethod || null,
      });
      toast.success(action === "aceitar" ? "Chamado aceito!" : "Mensagem enviada à Clima Branca.");
      setAcceptNote("");
      if (onChanged) onChanged(); else window.location.reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível registrar sua resposta");
    } finally {
      setAcceptBusy(false);
    }
  };

  const couponDiscount = Number(request.coupon_discount || 0);
  const finalValue = request.service_value != null
    ? Math.max(Number(request.service_value) - couponDiscount, 0)
    : null;
  const isQuote = !!request.is_quote;
  const quoteStatus = request.quote_status;
  const payAllowed = !isQuote || quoteStatus === "aprovado";

  const respondQuote = async (decision) => {
    setQuoteBusy(true);
    try {
      await api.post(`/requests/${request.id}/quote-response`, { decision, note: quoteNote || null });
      toast.success(decision === "aprovar" ? "Orçamento aprovado!" : "Orçamento recusado.");
      if (onChanged) { onChanged(); setQuoteBusy(false); }
      else window.location.reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível responder");
      setQuoteBusy(false);
    }
  };

  const payOnline = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/requests/${request.id}/checkout`, { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível iniciar o pagamento");
      setPaying(false);
    }
  };

  const downloadQuotePdf = async () => {
    try {
      const res = await api.get(`/requests/${request.id}/quote/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error("Não foi possível gerar o PDF do orçamento");
    }
  };

  return (
    <Card className="border-slate-200" data-testid={`request-card-${request.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800">{request.service_type}</span>
              {request.is_emergency && (
                <Badge className="bg-red-100 text-red-800 border-red-200 gap-1" data-testid={`emergency-badge-${request.id}`}>
                  <AlertTriangle className="h-3 w-3" /> Emergência
                </Badge>
              )}
              {request.is_warranty && (
                <Badge className="bg-green-100 text-green-800 border-green-200 gap-1">
                  <ShieldCheck className="h-3 w-3" /> Garantia
                </Badge>
              )}
              {request.warranty_approval_status && (
                <Badge className={`gap-1 border ${request.warranty_approval_status === "aprovado" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : request.warranty_approval_status === "reprovado" ? "bg-red-100 text-red-800 border-red-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}
                  data-testid={`warranty-status-${request.id}`}>
                  <ShieldCheck className="h-3 w-3" /> Garantia {request.warranty_approval_status}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">{request.equipment}{request.model ? ` · ${request.model}` : ""}</p>
          </div>
          <Badge className={`${meta.color} border`} data-testid={`request-status-${request.id}`}>
            {meta.label}
          </Badge>
        </div>

        {accMeta && (
          <Badge className={`${accMeta.color} border gap-1 w-fit`} data-testid={`acceptance-status-${request.id}`}>
            <Handshake className="h-3 w-3" /> {accMeta.label}
          </Badge>
        )}
        {request.budget_approval && (
          <Badge className={`border w-fit ${request.budget_approval === "aprovado" ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}`}
            data-testid={`budget-status-${request.id}`}>
            {request.budget_approval === "aprovado" ? "Orçamento aprovado" : "Orçamento não aprovado"}
          </Badge>
        )}

        {request.distance_km != null && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid={`distance-${request.id}`}>
            <MapPin className="h-3.5 w-3.5" /> ~{request.distance_km} km de você
          </div>
        )}

        {request.status !== "cancelado" && <StatusStepper status={request.status} id={request.id} />}

        {customerView && (acc === "pendente" || acc === "duvida") && request.status !== "cancelado" && (
          <div className="rounded-md border border-blue-200 bg-blue-50/70 p-3 space-y-2.5" data-testid={`acceptance-block-${request.id}`}>
            <p className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
              <Handshake className="h-4 w-4" /> Aguardando seu aceite
            </p>
            <p className="text-xs text-slate-600">
              Este chamado foi aberto pela Clima Branca. Confirme se está tudo certo ou envie uma dúvida/recomendação.
            </p>
            {acc === "duvida" && request.customer_acceptance_note && (
              <p className="text-xs text-blue-700 bg-white/70 rounded border border-blue-100 px-2 py-1" data-testid={`acceptance-last-note-${request.id}`}>
                Sua última mensagem: "{request.customer_acceptance_note}"
              </p>
            )}
            {!request.payment_method && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Forma de pagamento (opcional)</label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger className="h-9 bg-white" data-testid={`acceptance-payment-select-${request.id}`}>
                    <SelectValue placeholder="Selecione, se desejar informar" />
                  </SelectTrigger>
                  <SelectContent>
                    {(paymentOptions || PAYMENT_METHODS).map((m) => (
                      <SelectItem key={m} value={m} data-testid={`acceptance-payment-${m}-${request.id}`}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Textarea rows={2} value={acceptNote} onChange={(e) => setAcceptNote(e.target.value)}
              placeholder="Dúvida ou recomendação (opcional ao aceitar)" data-testid={`acceptance-note-${request.id}`} />
            <div className="flex flex-col sm:flex-row gap-2">
              <Button size="sm" disabled={acceptBusy} onClick={() => doAcceptance("aceitar")}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid={`acceptance-accept-${request.id}`}>
                {acceptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Aceitar chamado
              </Button>
              <Button size="sm" variant="outline" disabled={acceptBusy} onClick={() => doAcceptance("duvida")}
                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100 gap-1.5" data-testid={`acceptance-doubt-${request.id}`}>
                <HelpCircle className="h-4 w-4" /> Enviar dúvida
              </Button>
            </div>
          </div>
        )}

        {request.sla_breached && (
          <div className="flex items-center gap-1.5 rounded-md bg-red-50 border border-red-200 px-2 py-1.5 text-xs font-medium text-red-700"
            data-testid={`sla-alert-${request.id}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> SLA estourado: +24h sem técnico atribuído
          </div>
        )}

        <div className="space-y-1.5">
          {showCustomer && <Row icon={User}>{request.customer_name}{request.customer_phone ? ` · ${request.customer_phone}` : ""}</Row>}
          {request.franchisee_name && <Row icon={Store}>Franqueado: {request.franchisee_name}</Row>}
          {request.scheduled_date && (
            <Row icon={CalendarClock}>
              Agendado: {new Date(request.scheduled_date + "T00:00:00").toLocaleDateString("pt-BR")}{request.scheduled_time ? ` às ${request.scheduled_time}` : ""}
            </Row>
          )}
          {request.defect_description && <Row icon={Wrench}>{request.defect_description}</Row>}
          {request.observations && <Row icon={FileText}>Obs.: {request.observations}</Row>}
          {request.authorized_service && (
            <Row icon={ShieldCheck}>Serviço autorizado: {request.authorized_service === "OUTROS" ? `Outros${request.authorized_service_other ? ` — ${request.authorized_service_other}` : ""}` : request.authorized_service}</Row>
          )}
          <Row icon={Calendar}>Disponibilidade: {request.availability}</Row>
          <Row icon={MapPin}>
            {request.address_same_as_registered
              ? "Endereço do cadastro"
              : `${request.service_address || "-"}${request.service_cep ? ` · CEP ${request.service_cep}` : ""}`}
          </Row>
          {!request.address_same_as_registered && request.service_responsible_name && (
            <Row icon={User}>Recebe: {request.service_responsible_name}{request.service_responsible_phone ? ` · ${request.service_responsible_phone}` : ""}</Row>
          )}
          {request.assigned_technician_name && (
            <div className="flex items-center justify-between gap-2">
              <Row icon={Phone}>Técnico: {request.assigned_technician_name}{request.assigned_technician_phone ? ` · ${request.assigned_technician_phone}` : ""}</Row>
              {request.assigned_technician_phone && (
                <a href={waContactLink(request.assigned_technician_phone, `Olá ${request.assigned_technician_name}! Sobre o chamado ${request.service_type} da Clima Branca.`)}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs font-medium text-green-700 border border-green-200 bg-green-50 rounded-md px-2 py-1 hover:bg-green-100 flex-shrink-0"
                  data-testid={`tech-whatsapp-${request.id}`}>
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
            </div>
          )}
        </div>

        {(request.videos?.length > 0 || request.invoice) && (
          <div className="flex flex-wrap gap-2" data-testid={`media-block-${request.id}`}>
            {(request.videos || []).map((v, i) => (
              <a key={i} href={fileUrl(v)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50">
                <Film className="h-3.5 w-3.5 text-[#1e7be0]" /> Vídeo {i + 1}
              </a>
            ))}
            {request.invoice && (
              <a href={fileUrl(request.invoice)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50"
                data-testid={`invoice-link-${request.id}`}>
                <FileText className="h-3.5 w-3.5 text-[#1e7be0]" /> Nota fiscal
              </a>
            )}
          </div>
        )}

        {request.photos?.length > 0 && (
          <div className="flex gap-2 flex-wrap" data-testid={`photos-${request.id}`}>
            {request.photos.map((p, i) => (
              <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer">
                <img src={fileUrl(p)} alt="equipamento"
                  className="h-16 w-16 object-cover rounded-md border border-slate-200 hover:opacity-80" />
              </a>
            ))}
          </div>
        )}

        {canPayOnline && isQuote && (
          <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 space-y-2" data-testid={`quote-block-${request.id}`}>
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
              <FileText className="h-4 w-4" /> Orçamento
            </p>
            {Array.isArray(request.quote_items) && request.quote_items.length > 0 && (
              <div className="rounded bg-white border border-amber-100 divide-y divide-amber-50 text-sm" data-testid={`quote-items-${request.id}`}>
                {request.quote_items.map((it, i) => (
                  <div key={i} className="flex justify-between px-2.5 py-1.5">
                    <span className="text-slate-600">
                      {it.description}{Number(it.quantity) > 1 ? ` (x${it.quantity})` : ""}
                      <span className="ml-1 text-[10px] uppercase text-amber-500">{KIND_LABEL[it.kind] || ""}</span>
                    </span>
                    <span className="text-slate-700 font-medium">R$ {(Number(it.quantity || 1) * Number(it.unit_price || 0)).toFixed(2).replace(".", ",")}</span>
                  </div>
                ))}
                <div className="flex justify-between px-2.5 py-1.5 font-semibold text-slate-800">
                  <span>Total</span>
                  <span>R$ {Number(request.service_value || 0).toFixed(2).replace(".", ",")}</span>
                </div>
              </div>
            )}
            {Array.isArray(request.quote_items) && request.quote_items.length > 0 && (
              <Button size="sm" variant="outline" onClick={downloadQuotePdf}
                className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100" data-testid={`quote-pdf-${request.id}`}>
                <FileText className="h-3.5 w-3.5" /> Baixar orçamento (PDF)
              </Button>
            )}
            {quoteStatus === "aguardando_orcamento" && (
              <p className="text-sm text-amber-700" data-testid={`quote-waiting-${request.id}`}>
                Recebemos seu pedido. Em breve a equipe enviará o orçamento para sua aprovação.
              </p>
            )}
            {quoteStatus === "pendente" && (
              <div className="space-y-2" data-testid={`quote-pending-${request.id}`}>
                <p className="text-sm text-slate-700">
                  Valor proposto: <b>R$ {Number(finalValue ?? request.service_value).toFixed(2).replace(".", ",")}</b>. Deseja aprovar?
                </p>
                <Textarea rows={2} value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)}
                  placeholder="Observação (opcional)" data-testid={`quote-note-${request.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" disabled={quoteBusy} onClick={() => respondQuote("aprovar")}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid={`quote-approve-${request.id}`}>
                    <CheckCircle2 className="h-4 w-4" /> Aprovar
                  </Button>
                  <Button size="sm" variant="outline" disabled={quoteBusy} onClick={() => respondQuote("recusar")}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 gap-1.5" data-testid={`quote-reject-${request.id}`}>
                    <XCircle className="h-4 w-4" /> Recusar
                  </Button>
                </div>
              </div>
            )}
            {quoteStatus === "aprovado" && (
              <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid={`quote-approved-${request.id}`}>
                <CheckCircle2 className="h-4 w-4" /> Orçamento aprovado. Você já pode efetuar o pagamento abaixo.
              </p>
            )}
            {quoteStatus === "recusado" && (
              <p className="text-sm text-red-600 flex items-center gap-1.5" data-testid={`quote-rejected-${request.id}`}>
                <XCircle className="h-4 w-4" /> Orçamento recusado.{request.quote_note ? ` Obs.: ${request.quote_note}` : ""}
              </p>
            )}
          </div>
        )}

        {(request.payment_method || request.service_value != null || request.completion_photos?.length > 0) && (
          <div className="rounded-md bg-slate-50 border border-slate-100 p-2.5 space-y-2" data-testid={`payment-block-${request.id}`}>
            {request.service_value != null && (
              <p className="text-sm text-slate-700 flex items-center gap-1.5 flex-wrap">
                <DollarSign className="h-4 w-4 text-emerald-600" /> Valor: <b>R$ {Number(request.service_value).toFixed(2).replace(".", ",")}</b>
                {request.payment_status && (
                  <span className={`ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${request.payment_status === "pago" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                    data-testid={`payment-status-${request.id}`}>
                    {request.payment_status === "pago" ? "Pago" : "Pendente"}
                  </span>
                )}
              </p>
            )}
            {couponDiscount > 0 && (
              <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid={`coupon-line-${request.id}`}>
                <Ticket className="h-4 w-4" /> Cupom {request.coupon_code}: -R$ {couponDiscount.toFixed(2).replace(".", ",")}
                {finalValue != null && <span className="text-slate-700">· Total: <b>R$ {finalValue.toFixed(2).replace(".", ",")}</b></span>}
              </p>
            )}
            {canPayOnline && finalValue > 0 && request.payment_status !== "pago" && payAllowed && (
              <Button size="sm" onClick={payOnline} disabled={paying}
                className="w-full bg-[#0a2a6c] hover:bg-[#0f3d92] gap-1.5 mt-1" data-testid={`pay-online-button-${request.id}`}>
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Pagar com cartão ou boleto
              </Button>
            )}
            {request.payment_method && (
              <p className="text-sm text-slate-600">Pagamento: {request.payment_method}{request.payment_conditions ? ` · ${request.payment_conditions}` : ""}</p>
            )}
            {request.payment_method === "Pix" && (request.payment_pix_key || request.payment_pix_qr) && (
              <div className="flex items-center gap-3 pt-1" data-testid={`pix-block-${request.id}`}>
                {request.payment_pix_qr && (
                  <img src={fileUrl(request.payment_pix_qr)} alt="QR Pix"
                    className="h-24 w-24 object-contain rounded border border-slate-200 bg-white" data-testid={`pix-qr-${request.id}`} />
                )}
                <div className="text-xs text-slate-600 min-w-0">
                  <p className="flex items-center gap-1 font-medium text-slate-700"><QrCode className="h-3.5 w-3.5" /> Pague via Pix</p>
                  {request.payment_pix_key && <p className="break-all mt-1"><b>Chave:</b> {request.payment_pix_key}</p>}
                  {request.payment_pix_owner && <p><b>Recebedor:</b> {request.payment_pix_owner}</p>}
                </div>
              </div>
            )}
            {request.completion_photos?.length > 0 && (
              <div className="pt-1">
                <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Camera className="h-3.5 w-3.5" /> Fotos da conclusão</p>
                <div className="flex gap-2 flex-wrap" data-testid={`completion-photos-${request.id}`}>
                  {request.completion_photos.map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer">
                      <img src={fileUrl(p)} alt="conclusão"
                        className="h-16 w-16 object-cover rounded-md border border-slate-200 hover:opacity-80" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {request.rating && (
          <div className="rounded-md bg-amber-50 border border-amber-100 p-2" data-testid={`rating-display-${request.id}`}>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-4 w-4 ${request.rating >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              ))}
              <span className="text-xs text-slate-500 ml-1">Avaliação do cliente</span>
            </div>
            {request.rating_comment && <p className="text-sm text-slate-600 mt-1">"{request.rating_comment}"</p>}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-slate-400">
          <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{request.id.slice(0, 8)}</span>
          <span>{formatDate(request.created_at)}</span>
        </div>

        {actions && <div className="pt-1">{actions}</div>}

        {request.history?.length > 0 && (
          <div className="pt-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500"
              onClick={() => setOpen(!open)} data-testid={`history-toggle-${request.id}`}>
              {open ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
              Histórico ({request.history.length})
            </Button>
            {open && (
              <div className="mt-2 space-y-2 border-l-2 border-slate-100 pl-3">
                {request.history.map((h, i) => (
                  <div key={i} className="text-xs">
                    <p className="text-slate-700 font-medium">{(STATUS_META[h.status]?.label) || h.status}</p>
                    {h.note && <p className="text-slate-500">{h.note}</p>}
                    <p className="text-slate-400">{h.by} · {formatDate(h.at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
